import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type ComplianceStatus =
  | "NotStarted"
  | "InProgress"
  | "Completed"
  | "Skipped"
  | "NotApplicable"
  | "Overdue";

export type ComplianceCadence = "OneTime" | "Quarterly" | "Annual" | "Biennial";

export type ComplianceCategory =
  | "BoiFinCEN"
  | "StateAnnualReport"
  | "EstimatedTaxFederal"
  | "EstimatedTaxState"
  | "Form1099Nec"
  | "PayrollFederal"
  | "PayrollState"
  | "WorkersComp"
  | "BusinessLicense"
  | "Other";

export interface ComplianceItem {
  id: string;
  ownerUserId: string;
  title: string;
  description?: string | null;
  category: ComplianceCategory;
  cadence: ComplianceCadence;
  dueDate: string;
  status: ComplianceStatus;
  isHighPenalty: boolean;
  authorityUrl?: string | null;
  alertDaysBefore: number;
  completedOn?: string | null;
  confirmationNumber?: string | null;
}

const KEY = ["compliance", "list"];

export function useComplianceItems() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<ComplianceItem[]> => {
      const r = await api().get<ComplianceItem[]>("/api/compliance");
      return r.data;
    },
  });
}

export function useCompleteCompliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      completedOn?: string;
      confirmationNumber?: string;
    }) => {
      const r = await api().post<ComplianceItem>(`/api/compliance/${input.id}/complete`, {
        completedOn: input.completedOn,
        confirmationNumber: input.confirmationNumber,
      });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["advisor", "cards"] });
    },
  });
}
